// invodiff-vendors.jsx — vendor library: list of vendors with saved context + detail.
import { ConfirmModal } from './invodiff-jobs.jsx';
import React from 'react';
import {
  fmtUSD, fmtDelta, safeUrl, firestoreSafe, toCSV, downloadFile, relativeTime,
  invoiceDateOf, invoiceInRange, dateRangePresets, useFocusTrap,
  ROLES, ROLE_LABEL, ROLE_COLOR,
  usesSubInvoices, useJobsStore, useJobData, invoiceStore, deleteJobDeep, useVendorRules,
  normalizeUOM, isFootageUOM, stickFeet, itemFootage, convertToQuoteBasis, summarizeMatch,
  isAutoSku, itemKeyOf, levenshtein, fracToNum, normalizeForMatch, matchNormalize, matchLineItems,
  aggregateInvoices, compareInvoices, materialState, classifyRows, MATERIAL_STATE_META,
  getJobTrades, getSegments, lineTotal, subtotal, tax, grandTotal, pricesDiffer,
  statedInvoiceTotal, statedInvoiceTax, invoiceSpend,
  SAMPLE_VENDORS, INVOICE_A, INVOICE_B, SEED_VENDOR_RULES,
} from './invodiff-data.jsx';
import { Icon } from './invodiff-input.jsx';

function NewVendorModal({ onSave, onClose }) {
  const [name, setName] = React.useState('');
  const [note, setNote] = React.useState('');
  const canSave = name.trim() && note.trim();
  const dialogRef = React.useRef(null);
  useFocusTrap(dialogRef); // UX #5: trap focus + restore on close

  const handleKey = (e) => { if (e.key === 'Escape') onClose(); };

  return (
    <div className="vv-modal-overlay" role="presentation" onClick={onClose} onKeyDown={handleKey}>
      <div className="vv-modal" ref={dialogRef} role="dialog" aria-modal="true" aria-label="Add vendor context" onClick={(e) => e.stopPropagation()}>
        <div className="vv-modal-hd">
          <div className="vv-modal-title">Add vendor context</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close"><Icon name="x" size={12}/></button>
        </div>
        {/* UX #9 (forms): associate labels with inputs via htmlFor/id so clicking
            the label focuses the field and screen readers announce its name. */}
        <div className="vv-modal-body">
          <label className="vv-field-l" htmlFor="vv-new-name">Vendor name</label>
          <input
            id="vv-new-name"
            className="input"
            placeholder="e.g. Mesa Piping Supply"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <label className="vv-field-l" htmlFor="vv-new-note" style={{ marginTop: 16 }}>Expert note</label>
          <textarea
            id="vv-new-note"
            className="vv-note-ta"
            placeholder={'What should Claude know about how this vendor bills?\n\nExamples:\n• Always bills mobilization separately as "MOB FEE"\n• Tends to overbill labor hours on T&M jobs — verify against time sheets\n• Pre-negotiated 8% markup on copper materials is expected'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={6}
          />
          <div className="vv-field-hint">These notes are automatically included when Claude analyzes this vendor&rsquo;s invoices and comparisons.</div>
        </div>
        <div className="vv-modal-foot">
          {/* UX #10 (forms): tell the user why the submit is disabled instead of
              silently greying it out. */}
          {!canSave && (
            <span className="vv-field-hint" style={{ marginRight: 'auto' }}>
              Vendor name and note are both required.
            </span>
          )}
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave} onClick={() => canSave && onSave(name.trim(), note.trim())}>
            Add vendor
          </button>
        </div>
      </div>
    </div>
  );
}

function VendorsView({ vendorRules, setVendorRules, onCompareWithVendor, embedded = false }) {
  const vendors = Object.keys(vendorRules).filter((v) => (vendorRules[v] || []).length > 0).sort();
  const [activeVendor, setActiveVendor] = React.useState(vendors[0] || null);

  // Keep active in sync when list shrinks/grows
  React.useEffect(() => {
    if (vendors.length === 0) { setActiveVendor(null); return; }
    if (!activeVendor || !vendors.includes(activeVendor)) setActiveVendor(vendors[0]);
  }, [vendors.join('|')]);

  const removeRule = (vendor, ruleId) => {
    setVendorRules((prev) => {
      const next = { ...prev };
      next[vendor] = (next[vendor] || []).filter((r) => r.id !== ruleId);
      if (next[vendor].length === 0) delete next[vendor];
      return next;
    });
  };

  const clearVendor = (vendor) => {
    setVendorRules((prev) => {
      const next = { ...prev };
      delete next[vendor];
      return next;
    });
  };

  const handleAddNote = (vendor, text) => {
    setVendorRules((prev) => ({
      ...prev,
      [vendor]: [
        ...(prev[vendor] || []),
        { id: 'r' + Date.now(), added: new Date().toISOString().slice(0, 10), kind: 'note', pattern: 'Expert note', note: text },
      ],
    }));
  };

  const [showNewVendor, setShowNewVendor] = React.useState(false);

  const handleCreateVendor = (name, note) => {
    setVendorRules((prev) => ({
      ...prev,
      [name]: [
        ...(prev[name] || []),
        { id: 'r' + Date.now(), added: new Date().toISOString().slice(0, 10), kind: 'note', pattern: 'Expert note', note },
      ],
    }));
    setActiveVendor(name);
    setShowNewVendor(false);
  };

  if (vendors.length === 0) {
    return (
      <div className={`vv${embedded ? ' vv-embedded' : ''}`}>
        {!embedded && (
          <div className="vv-hero">
            <div className="vv-hero-tag mono">VENDOR CONTEXT LIBRARY</div>
            <h1 className="vv-hero-h">No saved vendor context yet.</h1>
            <p className="vv-hero-sub">
              Approve or reject a comparison with a note and it&rsquo;ll appear here, mapped to that vendor.
              Future comparisons against the same vendor pre-apply these rules automatically.
            </p>
          </div>
        )}
        <div className="vv-empty card">
          <div className="vv-empty-icon"><Icon name="rule" size={22}/></div>
          <div className="vv-empty-h">No vendor context yet</div>
          <div className="vv-empty-sub">Add notes about how each vendor bills so Claude can apply that knowledge automatically during comparisons and analysis.</div>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => setShowNewVendor(true)}>
            <Icon name="plus" size={13}/> Add first vendor
          </button>
        </div>
        {showNewVendor && <NewVendorModal onSave={handleCreateVendor} onClose={() => setShowNewVendor(false)} />}
        <VendorsStyles />
      </div>
    );
  }

  const ruleCount = (v) => (vendorRules[v] || []).length;
  const total = Object.values(vendorRules).reduce((s, list) => s + (list || []).length, 0);

  return (
    <div className={`vv${embedded ? ' vv-embedded' : ''}`}>
      {!embedded && (
        <div className="vv-hero">
          <div className="vv-hero-tag mono">VENDOR CONTEXT LIBRARY</div>
          <h1 className="vv-hero-h">Saved vendor context.</h1>
          <p className="vv-hero-sub">
            <span className="num">{vendors.length}</span> vendor{vendors.length === 1 ? '' : 's'} ·
            <span className="num">{' '}{total}</span> rule{total === 1 ? '' : 's'} on file.
            These pre-resolve matching variances on every new comparison.
          </p>
        </div>
      )}

      <div className="vv-layout">
        {/* Vendor tabs / list */}
        <div className="vv-tabs">
          <div className="vv-tabs-h">
            <span className="label">Vendors</span>
            <div className="vv-tabs-h-r">
              <span className="num vv-tabs-count">{vendors.length}</span>
              <button className="btn btn-ghost btn-xs" onClick={() => setShowNewVendor(true)} title="Add vendor">
                <Icon name="plus" size={12}/>
              </button>
            </div>
          </div>
          <div className="vv-tabs-list">
            {vendors.map((v) => (
              <button
                key={v}
                className={`vv-tab ${activeVendor === v ? 'is-on' : ''}`}
                onClick={() => setActiveVendor(v)}
              >
                <span className="vv-tab-name">{v}</span>
                <span className="vv-tab-count num">{ruleCount(v)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Active vendor detail */}
        {activeVendor && (
          <VendorDetail
            vendor={activeVendor}
            rules={vendorRules[activeVendor] || []}
            onRemove={(id) => removeRule(activeVendor, id)}
            onClear={() => clearVendor(activeVendor)}
            onCompare={() => onCompareWithVendor(activeVendor)}
            onAddNote={(text) => handleAddNote(activeVendor, text)}
          />
        )}
      </div>

      {showNewVendor && <NewVendorModal onSave={handleCreateVendor} onClose={() => setShowNewVendor(false)} />}
      <VendorsStyles />
    </div>
  );
}

// Static per-kind copy for the vendor-context panel — no local state, so it
// lives at module scope and stays a stable reference across renders.
const KIND_META = {
  ignore: { label: 'Ignore rules', desc: 'Variances on these patterns are auto-resolved.', tone: 'bad' },
  tolerance: { label: 'Tolerances', desc: 'Small drifts within bounds are auto-resolved.', tone: 'warn' },
  alias: { label: 'Item links', desc: 'Quote ↔ invoice lines learned from manual matches; matched automatically on every job.', tone: 'info' },
  note: { label: 'Vendor notes', desc: 'Contextual notes referenced during comparison.', tone: 'info' },
};

function VendorDetail({ vendor, rules, onRemove, onClear, onCompare, onAddNote }) {
  const [showNoteForm, setShowNoteForm] = React.useState(false);
  const [noteText, setNoteText] = React.useState('');
  const [confirmCfg, setConfirmCfg] = React.useState(null); // in-app confirm dialog

  const handleSaveNote = () => {
    if (!noteText.trim()) return;
    onAddNote(noteText.trim());
    setNoteText('');
    setShowNoteForm(false);
  };

  const byKind = {
    ignore: rules.filter((r) => r.kind === 'ignore'),
    tolerance: rules.filter((r) => r.kind === 'tolerance'),
    alias: rules.filter((r) => r.kind === 'alias'),
    note: rules.filter((r) => r.kind === 'note'),
  };
  return (
    <div className="vv-detail">
      <div className="vv-detail-hd">
        <div>
          <div className="vv-detail-tag mono">VENDOR</div>
          <div className="vv-detail-name">{vendor}</div>
        </div>
        <div className="vv-detail-actions">
          <button className="btn btn-sm" onClick={() => setShowNoteForm((s) => !s)}>
            <Icon name="plus" size={12}/> Add note
          </button>
          {/* UX #6: confirm before wiping every saved rule for this vendor. */}
          <button className="btn btn-sm" onClick={() => setConfirmCfg({
            title: 'Clear all saved context?',
            body: <>Every rule and note saved for &ldquo;<strong>{vendor}</strong>&rdquo; will be removed. This can&rsquo;t be undone.</>,
            confirmLabel: 'Clear context', tone: 'bad', icon: 'trash',
            onConfirm: onClear,
          })}>
            <Icon name="x" size={12}/> Clear context
          </button>
          <button className="btn btn-primary btn-sm" onClick={onCompare}>
            <Icon name="arrow" size={13}/> New comparison
          </button>
        </div>
      </div>

      {showNoteForm && (
        <div className="vv-note-form">
          <label className="vv-field-l" htmlFor="vv-note-ta">Expert note for {vendor}</label>
          <textarea
            id="vv-note-ta"
            className="vv-note-ta"
            placeholder={'What should Claude know about this vendor?\n\nExamples: "Always adds mobilization as MOB FEE", "Tends to overbill T&M labor — verify against time sheets", "Pre-negotiated 8% copper markup is expected"'}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={4}
            autoFocus
          />
          <div className="vv-note-actions">
            <button className="btn btn-sm btn-ghost" onClick={() => { setShowNoteForm(false); setNoteText(''); }}>Cancel</button>
            <button className="btn btn-sm btn-primary" disabled={!noteText.trim()} onClick={handleSaveNote}>Save note</button>
          </div>
        </div>
      )}

      <div className="vv-detail-stats">
        <div className="vv-stat"><div className="vv-stat-v num">{byKind.ignore.length}</div><div className="vv-stat-l">Ignore rules</div></div>
        <div className="vv-stat"><div className="vv-stat-v num">{byKind.tolerance.length}</div><div className="vv-stat-l">Tolerances</div></div>
        <div className="vv-stat"><div className="vv-stat-v num">{byKind.alias.length}</div><div className="vv-stat-l">Item links</div></div>
        <div className="vv-stat"><div className="vv-stat-v num">{byKind.note.length}</div><div className="vv-stat-l">Vendor notes</div></div>
      </div>

      {['ignore', 'tolerance', 'alias', 'note'].map((kind) => {
        const list = byKind[kind];
        if (list.length === 0) return null;
        const meta = KIND_META[kind];
        return (
          <div key={kind} className="vv-section">
            <div className="vv-section-hd">
              <div className="vv-section-l">
                <span className={`vrp-kind vrp-kind-${kind}`}>{kind}</span>
                <div>
                  <div className="vv-section-h">{meta.label}</div>
                  <div className="vv-section-sub">{meta.desc}</div>
                </div>
              </div>
              <span className="num vv-section-n">{list.length}</span>
            </div>
            <div className="vv-rule-list">
              {list.map((r) => (
                <div key={r.id} className="vv-rule">
                  <div className="vv-rule-l">
                    <span className="mono vv-rule-pat">{r.pattern}</span>
                    <span className="vv-rule-note">{r.note}</span>
                  </div>
                  <div className="vv-rule-r">
                    <span className="vv-rule-date mono">{r.added}</span>
                    <button className="btn btn-ghost btn-sm" title="Remove rule" aria-label="Remove rule"
                      /* UX #6: confirm single-rule deletion. */
                      onClick={() => setConfirmCfg({
                        title: 'Remove this rule?',
                        body: <>&ldquo;<strong>{r.pattern}</strong>&rdquo; will be removed from this vendor&rsquo;s saved context.</>,
                        confirmLabel: 'Remove rule', tone: 'bad', icon: 'trash',
                        onConfirm: () => onRemove(r.id),
                      })}>
                      <Icon name="x" size={12}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {confirmCfg && <ConfirmModal cfg={confirmCfg} onClose={() => setConfirmCfg(null)} />}
    </div>
  );
}

const VendorsStyles = () => (
  <style>{`
    .vv { max-width: 1180px; margin: 0 auto; padding: 48px 28px 80px; }
    /* Embedded inside the Settings disclosure — the section header already labels it. */
    .vv-embedded { padding: 4px 0 0; max-width: none; }
    .vv-embedded .vv-tabs { top: 12px; }

    .vv-hero { margin-bottom: 32px; max-width: 720px; }
    .vv-hero-tag { font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); margin-bottom: 12px; }
    .vv-hero-h { font-size: 30px; line-height: 1.1; letter-spacing: -0.025em; margin: 0 0 10px; font-weight: 600; }
    .vv-hero-sub { color: var(--fg-muted); font-size: 14px; line-height: 1.55; margin: 0; max-width: 620px; }
    .vv-hero-sub .num { color: var(--fg); font-weight: 500; padding: 0 2px; }

    .vv-empty { padding: 56px 32px; display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center; }
    .vv-empty-icon { width: 48px; height: 48px; border-radius: 50%; background: var(--surface-2); display: grid; place-items: center; color: var(--fg-dim); margin-bottom: 6px; }
    .vv-empty-h { font-size: 15px; font-weight: 500; }
    .vv-empty-sub { font-size: 13px; color: var(--fg-dim); }

    .vv-layout { display: grid; grid-template-columns: 280px 1fr; gap: 20px; align-items: flex-start; }

    .vv-tabs { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-lg); padding: 12px; box-shadow: var(--shadow-1); position: sticky; top: 70px; }
    .vv-tabs-count { font-size: 11px; color: var(--fg-dim); }
    .vv-tabs-list { display: flex; flex-direction: column; gap: 2px; padding-top: 8px; }
    .vv-tab {
      appearance: none; background: transparent; color: var(--fg-muted);
      border: 0; padding: 10px 10px; border-radius: var(--r);
      font: inherit; font-size: 13px; text-align: left; cursor: pointer;
      display: flex; align-items: center; justify-content: space-between; gap: 8px;
      transition: background .1s, color .1s;
    }
    .vv-tab:hover { background: var(--surface-2); color: var(--fg); }
    .vv-tab.is-on {
      background: oklch(from var(--accent) l c h / 0.14);
      color: var(--fg); font-weight: 500;
      box-shadow: inset 2px 0 0 var(--accent);
    }
    .vv-tab-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; min-width: 0; }
    .vv-tab-count { font-size: 11px; color: var(--fg-dim); background: var(--surface-2); padding: 1px 7px; border-radius: 999px; flex-shrink: 0; }
    .vv-tab.is-on .vv-tab-count { background: var(--bg); color: var(--fg-muted); }

    .vv-detail { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-lg); padding: 22px 24px; box-shadow: var(--shadow-1); }
    .vv-detail-hd { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 18px; border-bottom: 1px solid var(--border); margin-bottom: 18px; }
    .vv-detail-tag { font-size: 10.5px; letter-spacing: 0.12em; color: var(--fg-dim); }
    .vv-detail-name { font-size: 22px; font-weight: 600; letter-spacing: -0.015em; margin-top: 4px; }
    .vv-detail-actions { display: flex; gap: 8px; }

    .vv-detail-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 12px; margin-bottom: 24px; }
    .vv-stat { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r); padding: 12px 14px; box-shadow: var(--shadow-1); }
    .vv-stat-v { font-size: 22px; font-weight: 600; letter-spacing: -0.02em; color: var(--fg); }
    .vv-stat-l { font-size: 11.5px; color: var(--fg-dim); margin-top: 2px; letter-spacing: 0.04em; text-transform: uppercase; }

    .vv-section { margin-bottom: 20px; }
    .vv-section:last-child { margin-bottom: 0; }
    .vv-section-hd { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 10px; }
    .vv-section-l { display: flex; gap: 12px; align-items: flex-start; }
    .vv-section-l .vrp-kind { margin-top: 4px; }
    .vv-section-h { font-size: 13.5px; font-weight: 500; }
    .vv-section-sub { font-size: 12px; color: var(--fg-dim); margin-top: 2px; }
    .vv-section-n { font-size: 12px; color: var(--fg-dim); }

    .vv-rule-list { display: flex; flex-direction: column; gap: 0; border-top: 1px solid var(--border); }
    .vv-rule { display: flex; justify-content: space-between; gap: 16px; padding: 12px 0; border-bottom: 1px dashed var(--border); align-items: flex-start; }
    .vv-rule:last-child { border-bottom: none; }
    .vv-rule-l { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0; }
    .vv-rule-pat { font-size: 12px; color: var(--fg); background: var(--surface-2); padding: 2px 8px; border-radius: 4px; border: 1px solid var(--border); align-self: flex-start; }
    .vv-rule-note { font-size: 13px; color: var(--fg-muted); line-height: 1.5; }
    .vv-rule-r { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
    .vv-rule-date { font-size: 11px; color: var(--fg-dim); }

    .vv-tabs-h { display: flex; justify-content: space-between; align-items: center; padding: 4px 8px 10px; border-bottom: 1px solid var(--border); }
    .vv-tabs-h-r { display: flex; align-items: center; gap: 6px; }
    .btn-xs { padding: 3px 7px; font-size: 11px; }

    /* Inline note form */
    .vv-note-form { background: var(--surface-2); border: 1px solid var(--border); border-radius: var(--r); padding: 16px; margin-bottom: 20px; display: flex; flex-direction: column; gap: 10px; }
    .vv-field-l { font-size: 12px; font-weight: 500; color: var(--fg-muted); letter-spacing: 0.04em; text-transform: uppercase; }
    .vv-note-ta { width: 100%; box-sizing: border-box; background: var(--surface); border: 1px solid var(--border); border-radius: var(--r); color: var(--fg); font: inherit; font-size: 13px; line-height: 1.55; padding: 10px 12px; resize: vertical; outline: none; transition: border-color .15s; }
    .vv-note-ta:focus { border-color: var(--accent); }
    .vv-note-ta::placeholder { color: var(--fg-dim); }
    .vv-note-actions { display: flex; justify-content: flex-end; gap: 8px; }

    /* New vendor modal */
    .vv-modal-overlay { position: fixed; inset: 0; background: oklch(0 0 0 / 0.55); z-index: 200; display: grid; place-items: center; padding: 20px; }
    .vv-modal { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-lg); width: 100%; max-width: 520px; box-shadow: 0 20px 60px oklch(0 0 0 / 0.35); display: flex; flex-direction: column; }
    .vv-modal-hd { display: flex; justify-content: space-between; align-items: center; padding: 20px 20px 16px; border-bottom: 1px solid var(--border); }
    .vv-modal-title { font-size: 16px; font-weight: 600; letter-spacing: -0.01em; }
    .vv-modal-body { padding: 20px; display: flex; flex-direction: column; gap: 6px; }
    .vv-field-hint { font-size: 12px; color: var(--fg-dim); line-height: 1.5; margin-top: 2px; }
    .vv-modal-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 16px 20px; border-top: 1px solid var(--border); }

    @media (max-width: 860px) {
      .vv-layout { grid-template-columns: 1fr; }
      .vv-tabs { position: static; }
      .vv-tabs-list { flex-direction: row; flex-wrap: wrap; }
      .vv-tab { flex: 0 0 auto; }
    }
  `}</style>
);

export { VendorsView };
